import React from 'react';
import { Section } from './ui/Section';
import { XCircle, CheckCircle2 } from 'lucide-react';
export function EliteCoachDetails() {
  const comparisons = [{
    situation: 'Alumno con lumbalgia crónica',
    without: 'Improvisás o evitás trabajar con él',
    with: 'Protocolo exacto: qué hacer, qué no hacer, qué decir'
  }, {
    situation: 'Alumno con rodilla operada',
    without: 'No sabés qué está contraindicado',
    with: 'Lista de restricciones y ejercicios adaptados por condición'
  }, {
    situation: 'Alumno con sobrepeso significativo',
    without: 'Miedo a lesionar o quedar mal',
    with: 'Guía de adaptación completa con progresión segura'
  }, {
    situation: 'Alumno adulto mayor',
    without: 'Evitás todo por las dudas',
    with: 'Protocolo claro de qué sí y qué no según la condición'
  }, {
    situation: 'Situación de urgencia en clase',
    without: 'Pánico y sin saber qué hacer',
    with: 'Protocolo de emergencia paso a paso listo para usar'
  }, {
    situation: 'Queres llevar tus clases a otro nivel',
    without: 'No sabés cómo periodizar de forma avanzada',
    with: 'Sistema completo de periodización avanzada para resultados de élite'
  }];
  const modules = [{
    icon: '🦴',
    title: 'Módulo 1 — Columna y Espalda',
    desc: 'Lumbalgia crónica, hernia de disco, escoliosis, espondilolistesis y post-cirugía de columna. Qué hacer, qué evitar y qué decir en cada caso dentro del funcional.'
  }, {
    icon: '🦵',
    title: 'Módulo 2 — Miembros Inferiores',
    desc: 'Artrosis de rodilla, post-cirugía de rodilla y cadera, lesión de LCA, fascitis plantar. Adaptaciones WOD por WOD para seguir entrenando con seguridad.'
  }, {
    icon: '💪',
    title: 'Módulo 3 — Miembros Superiores',
    desc: 'Hombro lesionado, manguito rotador, túnel carpiano. Cómo modificar cada ejercicio de tren superior sin romper el WOD ni la dinámica de la clase.'
  }, {
    icon: '❤️',
    title: 'Módulo 4 — Condiciones Sistémicas',
    desc: 'Hipertensión arterial, diabetes tipo 2, obesidad, fibromialgia. Lo que podés hacer, lo que no podés hacer y las señales de alerta que requieren parar.'
  }, {
    icon: '📈',
    title: 'Módulo 5 — Periodización Avanzada',
    desc: 'Cómo diseñar macrociclos de alto rendimiento, WODs de competencia, picos de rendimiento programados y deload estratégico. Para el profe que quiere llevar sus alumnos al siguiente nivel.'
  }, {
    icon: '🚨',
    title: 'Módulo 6 — Herramientas de Seguridad',
    desc: 'Checklist de banderas rojas en el WOD. Tabla maestra de contraindicaciones. Protocolo de emergencia listo para imprimir. WODs de competencia listos. Consentimiento informado editable.'
  }];
  return <Section bg="dark-800" data-id="element-68">
      <div className="text-center mb-8 md:mb-16" data-id="element-69">
        <h2 className="text-2xl md:text-4xl font-display font-bold text-white mb-3 md:mb-6 uppercase" data-id="element-70">
          ¿Qué agrega el{' '}
          <span className="text-lime-400" data-id="element-71">Elite Coach 360™</span>?
        </h2>
        <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-72">
          El sistema avanzado que hace que ningún alumno sea "demasiado difícil"
        </p>
      </div>

      <div className="max-w-5xl mx-auto mb-10 md:mb-20" data-id="element-73">
        {/* Mobile card layout */}
        <div className="flex flex-col gap-3 md:hidden" data-id="element-74">
          {comparisons.map((row, i) => <div key={i} className="bg-dark-900 border border-dark-700 rounded-xl p-4" data-id="element-75">
              <div className="text-sm font-bold text-white mb-2" data-id="element-76">
                {row.situation}
              </div>
              <div className="flex items-start gap-2 mb-2" data-id="element-77">
                <XCircle className="w-4 h-4 text-red-500/70 flex-shrink-0 mt-0.5" data-id="element-78" />
                <span className="text-xs text-gray-400" data-id="element-79">{row.without}</span>
              </div>
              <div className="flex items-start gap-2" data-id="element-80">
                <CheckCircle2 className="w-4 h-4 text-lime-500 flex-shrink-0 mt-0.5" data-id="element-81" />
                <span className="text-xs text-white font-medium" data-id="element-82">
                  {row.with}
                </span>
              </div>
            </div>)}
        </div>

        {/* Desktop table layout */}
        <div className="hidden md:block overflow-x-auto" data-id="element-83">
          <table className="w-full text-left border-collapse" data-id="element-84">
            <thead data-id="element-85">
              <tr data-id="element-86">
                <th className="p-4 border-b-2 border-dark-700 text-white font-display text-lg w-1/3" data-id="element-87">
                  Situación
                </th>
                <th className="p-4 border-b-2 border-dark-700 text-gray-400 font-display text-lg w-1/3" data-id="element-88">
                  Sin Elite Coach 360
                </th>
                <th className="p-4 border-b-2 border-lime-500 text-lime-400 font-display text-lg w-1/3 bg-lime-500/5 rounded-t-xl" data-id="element-89">
                  Con Elite Coach 360
                </th>
              </tr>
            </thead>
            <tbody data-id="element-90">
              {comparisons.map((row, i) => <tr key={i} className="border-b border-dark-700/50 hover:bg-dark-700/30 transition-colors" data-id="element-91">
                  <td className="p-4 text-gray-300 font-medium" data-id="element-92">
                    {row.situation}
                  </td>
                  <td className="p-4 text-gray-400" data-id="element-93">
                    <div className="flex items-start gap-2" data-id="element-94">
                      <XCircle className="w-5 h-5 text-red-500/70 flex-shrink-0 mt-0.5" data-id="element-95" />
                      <span data-id="element-96">{row.without}</span>
                    </div>
                  </td>
                  <td className="p-4 text-white bg-lime-500/5" data-id="element-97">
                    <div className="flex items-start gap-2" data-id="element-98">
                      <CheckCircle2 className="w-5 h-5 text-lime-500 flex-shrink-0 mt-0.5" data-id="element-99" />
                      <span data-id="element-100">{row.with}</span>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="max-w-5xl mx-auto" data-id="element-101">
        <h3 className="text-xl md:text-2xl font-display font-bold text-white mb-6 md:mb-10 text-center flex items-center justify-center gap-2 md:gap-3" data-id="element-102">
          <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8 text-lime-500" data-id="element-103" />
          ELITE COACH 360™ INCLUYE:
        </h3>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-id="element-104">
          {modules.map((mod, i) => <div key={i} className="bg-dark-900 border border-dark-700 p-4 md:p-6 rounded-2xl flex gap-3 md:gap-4 hover:border-lime-500/30 transition-colors" data-id="element-105">
              <div className="text-2xl md:text-3xl flex-shrink-0" data-id="element-106">
                {mod.icon}
              </div>
              <div data-id="element-107">
                <h4 className="text-base md:text-lg font-bold text-white mb-1 md:mb-2" data-id="element-108">
                  {mod.title}
                </h4>
                <p className="text-gray-400 text-xs md:text-sm leading-relaxed" data-id="element-109">
                  {mod.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </Section>;
}